import { Checkbox, Chip, FormControl, FormHelperText, FormLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import HelpTooltip from '../HelpTooltip';
import { BiHelpCircle } from 'react-icons/bi';
import { useEffect, useState } from 'react';
import { Close, QueryStats } from '@mui/icons-material';
import { FbeOption } from '../../services/fbeTypes';
import FieldLock from '../FieldLock';
import ImageTooltip from '../ImageTooltip';

export interface MultiSelectProps {
    labelText: string;
    labelId: string;
    mandatory: boolean;
    helpTextTitle: string;
    helpTextBody: string;
    onChange: (value: string[]) => void;
    options: FbeOption[];
    selectedValues: FbeOption[];
    disabled: boolean;
    fullWidth: boolean;
    error?: boolean;
    helperText?: string;

    onLock: (field: string, locked: boolean) => void;
    disabledLock: boolean;
    displayedLock: boolean;
    isLocked: boolean;

    showWorkflowInfo?: boolean;
}

const MultiSelect = (props: MultiSelectProps): JSX.Element => {
    const [selectedOptionValues, setSelectedOptionValues] = useState<string[]>([]);
    const [open, setOpen] = useState(false);

    useEffect(() => {
      if(props.selectedValues.length > 0){
        setSelectedOptionValues(props.selectedValues.map((opt) => opt.value));
      } else{
        setSelectedOptionValues([]);
      }
      }, [props.selectedValues]);

    const handleInputChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[];

        setSelectedOptionValues(values);

        const updateOptions = props.options.filter(option => values.includes(option.value)
                                    );
        const updatedValues = updateOptions.map(option => option.value)

        props.onChange(updatedValues);

        setOpen(false);
    }

    const handleDelete = (value: string) => {
        const updatedValues = selectedOptionValues.filter((selected) => selected !== value);
        setSelectedOptionValues(updatedValues);
        props.onChange(updatedValues);
        setOpen(false);
    }

    const handleClose = () => {
        setOpen(false);
    }

    return (
        <FormControl fullWidth={props.fullWidth} error={props.error || false}>
        <FormLabel component="legend" sx={{fontSize:'12px', mb:1.5}} htmlFor={props.labelId}>
        <FieldLock
            onChange={function (locked: boolean): void {
                props.onLock(props.labelId,locked);
            } }
            labelId={props.labelId}
            disabled={props.disabledLock}
            displayed={props.displayedLock}
            value={props.isLocked}/>
         {props.labelText} <b>{props.mandatory ? '*' : ''}</b> <HelpTooltip title={props.helpTextTitle} body={props.helpTextBody}> <BiHelpCircle/> </HelpTooltip>
            {props.showWorkflowInfo !== undefined && props.showWorkflowInfo ? (
              <ImageTooltip image={'workflow.png'} imageAltText={'workflow image overview'}> <QueryStats sx={{ fontSize: 16 }}/> </ImageTooltip>
            ) : (<></>)}
        </FormLabel>
        <Select
          id={props.labelId}
          name={props.labelId}
          data-testid={props.labelId}
          multiple
          sx={{bgcolor:'#FFFFFF'}}
          value={selectedOptionValues}
          onChange={handleInputChange}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={handleClose}
          inputProps={{
            'aria-label': props.labelText,
            autoComplete: 'off'
          }}
          renderValue={(selected) => (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {(selected as string[]).map((value) => {
                const option = props.options.find((opt) => opt.value === value);
                return option ? <Chip
                    id={value}
                    key={value}
                    label={option.label}
                    disabled={props.disabled}
                    onDelete={() => handleDelete(value)}
                    deleteIcon={<Close sx={{
                        color: 'white',
                        fontSize:'14px',
                        borderRadius: '16px',
                        bgcolor: 'lightgray',
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.3)',
                        },
                        '& .MuiChip-deleteIcon': {
                          color: 'white',
                          '&:hover': {
                            color: '#ffeb3b',
                          },
                        }
                    }} />}
                    sx={{ margin: '2px' }}
                    onMouseDown={(event) => {
                        event.stopPropagation();
                    }}
                /> : null;
              })}
            </div>
          )}
          defaultValue={[]}
          fullWidth={props.fullWidth}
          disabled={props.disabled}
        >
        {props.options.map((option) => (
          <MenuItem id={`mi${option.value}`} key={option.value} value={option.value} data-testid={`mi${option.value}`} disabled={option.disabled}>
            {
              option.label.startsWith('==') ? (<></>) : (
                <Checkbox id={`cb${option.value}`} checked={selectedOptionValues.length > 0 && selectedOptionValues.includes(option.value)} key={`${option.value}}`} data-testid={`cb${option.value}`} disabled={option.disabled} />
              )
            }


            {option.label}
          </MenuItem>
        ))}
       </Select>
       {props.helperText && <FormHelperText>{props.helperText}</FormHelperText>}
      </FormControl>
    );
  };

export default MultiSelect;